import { withDependencies } from '@wix/thunderbolt-ioc'
import { IFontsLoadingState, FontsStatusHandlers, IFontsStatusNotifier } from './types'
import { FontsLoadingStateSymbol, name, FontsStatusNotifierSymbol } from './symbols'
import { createPromise } from '@wix/thunderbolt-commons'
import { IModelUpdatesHandlersImplementor } from 'ds-feature-model-updates-invoker'
import { IDataRequirementsChecker, IPropsStore, Props } from '@wix/thunderbolt-symbols'
import _ from 'lodash'

const FONT_LOAD_TIMEOUT = 10 * 1000

export const FontLoadStatus = withDependencies(
	[Props, FontsLoadingStateSymbol, FontsStatusNotifierSymbol],
	(
		props: IPropsStore,
		featureState: IFontsLoadingState,
		fontsStatusNotifier: IFontsStatusNotifier
	): IDataRequirementsChecker & IModelUpdatesHandlersImplementor<FontsStatusHandlers> => {
		const initialState = { fontsLoadStatus: {}, isFirstFontsLoading: true }
		featureState.update(() => initialState)

		const markFontAsLoaded = (fontFamily: string) => {
			featureState.update(({ loadedFonts, fontsLoadStatus, ...rest }) => {
				fontsLoadStatus[fontFamily].resolver?.(null)
				return { ...rest, fontsLoadStatus, loadedFonts: { ...loadedFonts, [fontFamily]: true } }
			})
			fontsStatusNotifier.notifyFontStatusChange()
		}

		const markFontAsTimedOut = (fontFamily: string) => {
			featureState.update(({ timedOutFonts, fontsLoadStatus, ...rest }) => {
				fontsLoadStatus[fontFamily].resolver?.(null)
				return { ...rest, fontsLoadStatus, timedOutFonts: { ...timedOutFonts, [fontFamily]: true } }
			})
			fontsStatusNotifier.notifyFontStatusChange()
		}

		const isLoadedOrTimedOut = (fontFamily: string) =>
			_.get(featureState.get(), ['loadedFonts', fontFamily]) ||
			_.get(featureState.get(), ['timedOutFonts', fontFamily])

		props.update({ FONTS: { onFontLoaded: markFontAsLoaded } })

		return {
			featureName: name,
			handlers: {
				updateFonts: ({ fonts, viewMode }) => {
					props.update({ FONTS: { fontsList: [...fonts], viewMode } })
				},
				createFontLoadStatusPromise: ({ fontFamily, fontsLoadExperiment }) => {
					if (fontsLoadExperiment) {
						featureState.update(({ fontsLoadStatus, loadedFonts, timedOutFonts, ...rest }) => ({
							...rest,
							loadedFonts: { ...loadedFonts, [fontFamily]: false },
							timedOutFonts: { ...timedOutFonts, [fontFamily]: false },
							fontsLoadStatus: { ...fontsLoadStatus, [fontFamily]: createPromise() },
						}))
					} else {
						featureState.update(({ fontsLoadStatus, ...rest }) => ({
							...rest,
							fontsLoadStatus: { [fontFamily]: createPromise(), ...fontsLoadStatus },
						}))
					}

					setTimeout(() => {
						if (!isLoadedOrTimedOut(fontFamily)) {
							markFontAsTimedOut(fontFamily)
						}
					}, FONT_LOAD_TIMEOUT)
				},
				resetFontsLoadingStatus: (_viewMode: string) => {
					featureState.update(({ ...rest }) => ({ ...rest, loadedFonts: {}, timedOutFonts: {} }))
				},
			},
			pendingRequirements: () => {
				const { fontsLoadStatus, isFirstFontsLoading } = featureState.get() || initialState
				const fontsLoadStatusValues = Object.values(fontsLoadStatus)
				if (isFirstFontsLoading) {
					fontsLoadStatusValues.forEach((fontLoadStatus) => fontLoadStatus.resolver(null))
					featureState.update((state) => ({
						...state,
						isFirstFontsLoading: false,
					}))
					return Promise.resolve()
				}

				const fontLoadPromises = fontsLoadStatusValues.map((fontLoadStatus) => fontLoadStatus.promise)
				return Promise.all(fontLoadPromises).then(() => Promise.resolve())
			},
		}
	}
)
